import { z } from "zod";
import {
  DiagnosisSchema,
  ParticipantTypeSchema,
  StudyDesignSchema,
  XrModalitySchema,
} from "./study.js";

// ─── Shared extraction fields ────────────────────────────────────────────────

export const SharedExtractionSchema = z.object({
  // Population
  diagnoses: z.array(DiagnosisSchema),
  sampleSize: z.number().int().nullable(),
  ageRangeMin: z.number().nullable(),
  ageRangeMax: z.number().nullable(),
  country: z.string().nullable(),

  // Participant type & study design
  participantType: ParticipantTypeSchema,
  studyDesign: StudyDesignSchema,

  // XR details
  xrModality: XrModalitySchema,
  deviceName: z.string().nullable(),
  deviceManufacturer: z.string().nullable(),
  deliveryMode: z.enum(["STANDALONE", "TETHERED", "NOT_SPECIFIED"]),
  eyeTrackingIntegrated: z.boolean().nullable(),

  // Terminology
  neglectTerminologyUsed: z.string().nullable(),

  // Funding
  fundingSource: z.string().nullable(),

  // Reviewer-defined subtopic values, keyed by ExtractionTopic.key
  customTopics: z.record(z.string(), z.string().nullable()).optional(),
});
export type SharedExtraction = z.infer<typeof SharedExtractionSchema>;

// ─── Assessment review ───────────────────────────────────────────────────────

export const AssessmentExtractionSchema = SharedExtractionSchema.extend({
  reviewType: z.literal("ASSESSMENT"),
  assessmentType: z.enum(["NEGLECT_TEST_REPLICATED", "NOVEL_TASK", "ADL_SIMULATION"]).nullable(),
  comparisonCondition: z
    .enum([
      "CONVENTIONAL_LINE_BISECTION",
      "CONVENTIONAL_CANCELLATION",
      "CONVENTIONAL_FULL_BATTERY",
      "CONVENTIONAL_OTHER",
      "HEALTHY_CONTROLS_SAME_VR",
      "NONE",
      "OTHER",
    ])
    .nullable(),
  comparisonDetail: z.string().nullable(),
  validityEvidence: z.array(z.enum(["CRITERION", "CONSTRUCT", "CONTENT"])),
  reliabilityMetrics: z.string().nullable(),
  performanceMeasures: z.string().nullable(),
  dropoutRate: z.number().nullable(),
  adverseEventsReported: z.boolean().nullable(),
  tolerabilityNotes: z.string().nullable(),
  usabilityRating: z.string().nullable(),
});
export type AssessmentExtraction = z.infer<typeof AssessmentExtractionSchema>;

// ─── Treatment review ────────────────────────────────────────────────────────

export const TreatmentExtractionSchema = SharedExtractionSchema.extend({
  reviewType: z.literal("TREATMENT"),
  interventionDescription: z.string().nullable(),
  vrTaskType: z.string().nullable(),
  durationPerSession: z.number().nullable(),
  sessionsPerWeek: z.number().nullable(),
  totalSessions: z.number().nullable(),
  comparisonCondition: z.string().nullable(),
  primaryOutcomeMeasure: z.string().nullable(),
  effectSize: z.string().nullable(),
  pValue: z.string().nullable(),
  robAssessmentType: z.enum(["ROB2", "ROBINS_I"]).nullable(),
  robOverallJudgement: z
    .enum(["LOW", "SOME_CONCERNS", "HIGH"])
    .nullable(),
  robNotes: z.string().nullable(),
});
export type TreatmentExtraction = z.infer<typeof TreatmentExtractionSchema>;

// ─── Technical/design-feature mapping ────────────────────────────────────────

export const TechnicalExtractionSchema = SharedExtractionSchema.extend({
  reviewType: z.literal("TECHNICAL"),
  softwareType: z
    .enum(["CUSTOM_BUILT", "COMMERCIAL", "OPEN_SOURCE", "NOT_SPECIFIED"])
    .nullable(),
  softwareName: z.string().nullable(),
  interactionModalities: z.array(
    z.enum(["GAZE", "CONTROLLER", "GESTURE", "TOUCHSCREEN", "OTHER"])
  ),
  feedbackTypes: z.array(
    z.enum(["VISUAL", "AUDITORY", "HAPTIC", "MULTIMODAL"])
  ),
  outcomeMeasuresCaptured: z.string().nullable(),
});
export type TechnicalExtraction = z.infer<typeof TechnicalExtractionSchema>;

export const ExtractionDataSchema = z.discriminatedUnion("reviewType", [
  AssessmentExtractionSchema,
  TreatmentExtractionSchema,
  TechnicalExtractionSchema,
]);
export type ExtractionData = z.infer<typeof ExtractionDataSchema>;

// ─── Extraction record ───────────────────────────────────────────────────────

export const ExtractionRecordSchema = z.object({
  id: z.string().uuid(),
  studyId: z.string().uuid(),
  reviewId: z.string().uuid(),
  extractedById: z.string().uuid(),
  data: ExtractionDataSchema,
  isAiSuggested: z.boolean(),
  isConfirmed: z.boolean(),
  version: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ExtractionRecord = z.infer<typeof ExtractionRecordSchema>;

// ─── Screening decision ───────────────────────────────────────────────────────

export const ScreeningPhaseSchema = z.enum(["TITLE_ABSTRACT", "FULL_TEXT"]);
export type ScreeningPhase = z.infer<typeof ScreeningPhaseSchema>;

export const ScreeningDecisionValueSchema = z.enum([
  "INCLUDE",
  "EXCLUDE",
  "UNCERTAIN",
]);
export type ScreeningDecisionValue = z.infer<typeof ScreeningDecisionValueSchema>;

export const ScreeningDecisionSchema = z.object({
  id: z.string().uuid(),
  studyId: z.string().uuid(),
  reviewId: z.string().uuid(),
  reviewerId: z.string().uuid(),
  phase: ScreeningPhaseSchema,
  decision: ScreeningDecisionValueSchema,
  exclusionReason: z.string().nullable(),
  isAiSuggested: z.boolean(),
  aiConfidenceScore: z.number().min(0).max(1).nullable(),
  overrideReason: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type ScreeningDecision = z.infer<typeof ScreeningDecisionSchema>;

export const CriterionCheckSchema = z.object({
  id: z.string().max(10),                       // "I1", "E2"
  kind: z.enum(["INCLUSION", "EXCLUSION"]),
  criterion: z.string().max(2000),
  ai: z.enum(["YES", "NO", "UNCLEAR"]).nullable(), // AI verdict shown at the time
  reviewer: z.enum(["YES", "NO", "UNCLEAR"]),   // INCLUSION: met / not met; EXCLUSION: applies / does not apply; UNCLEAR either way
});
export type CriterionCheck = z.infer<typeof CriterionCheckSchema>;

export const CreateScreeningDecisionSchema = z.object({
  studyId: z.string().uuid(),
  reviewId: z.string().uuid(),
  phase: ScreeningPhaseSchema,
  decision: ScreeningDecisionValueSchema,
  exclusionReason: z.string().nullable().optional(),
  exclusionReasonCodes: z.array(z.string()).optional(),
  inclusionReason: z.string().nullable().optional(),
  inclusionReasonCodes: z.array(z.string()).optional(),
  uncertainReason: z.string().nullable().optional(),
  overrideReason: z.string().nullable().optional(),
  // The reviewer's own per-criterion marks from the AI criteria table, stored
  // next to the AI's verdict so agreement can be audited per criterion.
  criteriaChecks: z.array(CriterionCheckSchema).max(40).optional(),
});
export type CreateScreeningDecision = z.infer<typeof CreateScreeningDecisionSchema>;
